import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { getAlarmsAndNotificationsUseCase } from "@src/cloud-connect/support/use-cases/get-alarms-notifications-use-case";
import {
  updateAddRequestTechnicalSupportDrawerModal,
  updateAddRequestTechnicalSupportDrawerModalWithoutMachine,
  updateModalsCloseDrawerModal,
} from "@src/reducers/your-reducer";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import {
  EventSeverityBand,
  AlarmNotificationOPCUAEventContext,
} from "@src/types/support";

import severityUrgentIcon from "@src/assets/svg/notification-severity-urgent-red.svg";
import severityWarningIcon from "@src/assets/svg/notification-severity-warning-circle-orange.svg";
import severityInfoIconBlue from "@src/assets/svg/notification-severity-info-blue.svg";
import severityInfoIconRed from "@src/assets/svg/notification-severity-info-red.svg";
import severityInfoIconOrange from "@src/assets/svg/notification-severity-info-orange.svg";
import checkBoxIcon from "@src/assets/svg/checkbox-icon.svg";
import checkedCheckboxIcon from "@src/assets/svg/checked-checkbox-icon.svg";

export type SeverityFilterOption = "all" | EventSeverityBand;

const SEVERITY_ICON_MAP = {
  urgent: [severityUrgentIcon, severityInfoIconRed],
  warning: [severityWarningIcon, severityInfoIconOrange],
  info: [severityInfoIconBlue],
};

export const useAlarmsNotificationsDrawerModalHook = () => {
  const dispatch = useDispatch();

  const [notifications, setNotifications] = useState<
    AlarmNotificationOPCUAEventContext[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState<SeverityFilterOption[]>([
    "all",
  ]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterToggleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchNotifications = async () => {
      setIsLoading(true);

      const result = await getAlarmsAndNotificationsUseCase();

      if (!result.success) {
        errorSnackbar(result.error);
      } else {
        setNotifications(result.data);
      }

      setIsLoading(false);
    };

    fetchNotifications();
  }, []);

  const getSeverityBand = useCallback(
    (notification: AlarmNotificationOPCUAEventContext): EventSeverityBand => {
      if (notification.level === "critical") return "urgent";
      if (notification.level === "warning") return "warning";
      if (notification.level === "info") return "info";

      if (notification.severity > 750) return "urgent";
      if (notification.severity >= 500) return "warning";

      return "info";
    },
    [],
  );

  const getSeverityIcon = (severityBand: EventSeverityBand) =>
    SEVERITY_ICON_MAP[severityBand];

  const toggleSeverityFilter = (value: SeverityFilterOption) => {
    setSeverityFilter((prev) => {
      if (value === "all") return ["all"];

      const filters = prev.filter((f) => f !== "all");

      if (filters.includes(value)) {
        const next = filters.filter((f) => f !== value);
        return next.length ? next : ["all"];
      }

      const next = [...filters, value];
      const allSeverities: EventSeverityBand[] = ["urgent", "warning", "info"];
      const areAllSeveritiesSelected = allSeverities.every((severity) =>
        next.includes(severity),
      );

      return areAllSeveritiesSelected ? ["all"] : next;
    });
  };

  const filteredNotifications = useMemo(() => {
    if (severityFilter.includes("all")) return notifications;

    return notifications.filter((notification) =>
      severityFilter.includes(getSeverityBand(notification)),
    );
  }, [notifications, severityFilter, getSeverityBand]);

  const openSupportDrawer = (
    notification: AlarmNotificationOPCUAEventContext,
  ) => {
    if (notification.data_source_name) {
      dispatch(
        updateAddRequestTechnicalSupportDrawerModal({
          source_id: notification.data_source_id,
          source_dsn: notification.source_node,
          source_name: notification.data_source_name,
          notification_information: notification,
        }),
      );
    } else {
      dispatch(
        updateAddRequestTechnicalSupportDrawerModalWithoutMachine({
          source_id: notification.data_source_id,
          notification_information: notification,
        }),
      );
    }

    const dynamicAddRequestTechnicalSupportDrawerModalWithMachineOrWithoutMachineId =
      notification.data_source_name
        ? "addRequestTechnicalSupportDrawerModal"
        : "addRequestTechnicalSupportDrawerModalWithoutMachine";

    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "alarmsNotificationsDrawerModal",
      }),
    );

    dispatch(
      updateModalsCloseDrawerModal({
        showModal: true,
        id: dynamicAddRequestTechnicalSupportDrawerModalWithMachineOrWithoutMachineId,
      }),
    );
  };

  const getFilterCheckboxIconByState = (isSelected: boolean) =>
    isSelected ? checkedCheckboxIcon : checkBoxIcon;

  useEffect(() => {
    if (!isFilterOpen) {
      return undefined;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (
        filterToggleRef.current &&
        !filterToggleRef.current.contains(event.target as Node)
      ) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isFilterOpen]);

  return {
    filteredNotifications,
    getSeverityBand,
    getSeverityIcon,
    isLoading,
    openSupportDrawer,
    severityFilter,
    toggleSeverityFilter,
    getFilterCheckboxIconByState,
    isFilterOpen,
    setIsFilterOpen,
    filterToggleRef,
  };
};
